/* IMPORTS */
import React, { useCallback, useEffect, useState } from "react";
import { DatasetList } from "./DatasetList";
import moment, { Moment } from "moment";
import gettext from "../../../shared/utils/gettext";
import { ButtonGroup, Dropdown, DropdownButton, ToggleButton } from "react-bootstrap";
import "./CorrelationStyling.css";
import { DataTimeline } from "../shared/DataTimeline";
import cropr, { GeometryModel } from "../../../shared/utils/cropr";
import { $ } from "../../../shared/utils/legacy";
import {
  DatasetTypes,
  MeasurementNameIdTypes,
  MeasurementTypes,
  RoiTypes,
  SelectedMeasurementsObjectTypes,
} from "./ObjectInterfaces";

/* Assigning the prop types */
interface CorrelationToolDataSelectionProps {
  datasets: DatasetTypes[];
  did: string;
  onClick: any;
  setKey: any;
  getDisplayName: any;
}

/* Local type interfaces */
interface UnixDataTypes {
  [key: string]: UnixTypes;
}

interface UnixTypes {
  unix: number;
  ds?: DatasetTypes[];
}

/* CorrelationToolDataSelection Component */
export const CorrelationToolDataSelection = ({
  datasets,
  did,
  onClick,
  setKey,
  getDisplayName,
}: CorrelationToolDataSelectionProps) => {
  const [graphData, setGraphData] = useState<UnixTypes[]>([]);
  const [growthChecked, setGrowthChecked] = useState<boolean>(false);
  const [heightChecked, setHeightChecked] = useState<boolean>(false);
  const [ignoredData, setIgnoredData] = useState<string[]>(["soil_type"]);
  const [initBrushState, setInitBrushState] = useState<any>();
  const [mainMeasurement, setMainMeasurement] = useState<MeasurementNameIdTypes>({
    name: "No Preference",
    layerId: 0,
    displayName: "No Preference",
  });
  const [numOfTopCors, setNumOfTopCors] = useState<number>(10);
  const [notEnoughDataError, setNotEnoughDataError] = useState<JSX.Element>(<p />);
  const [selectedDs, setSelectedDs] = useState<DatasetTypes[]>([]);
  const [selectedMeasurements, setSelectedMeasurements] = useState<SelectedMeasurementsObjectTypes>(
    {}
  );
  const [selectedNames, setSelectedNames] = useState<MeasurementNameIdTypes[]>([]);

  /* Hook filtering, processing and sorting the data */
  useEffect(() => {
    /* Filtering data */
    let filtered: DatasetTypes[] = [];
    datasets.forEach((ds: DatasetTypes) => {
      if (!ignoredData.includes(ds.data_type)) {
        filtered.push(ds);
      }
    });
    if (filtered.length === 0) {
      setGraphData([]);
      setSelectedDs([]);
      return;
    }

    /* Getting date info */
    const datasetsByDate: UnixDataTypes = filtered.reduce(
      (memo: UnixDataTypes, ds: DatasetTypes) => {
        const unix: number = moment(ds.recording_datetime).startOf("day").unix();
        if (!memo[unix]) {
          const clazz: string = determineDsClass(ds);

          /* Figure out why type Unix does not work with d[clazz] = 0 */
          const d: any = {
            unix: unix,
            ds: [ds],
          };
          d[clazz] = 0;
          memo[unix] = d;
        } else {
          memo[unix].ds!.push(ds);
        }
        return memo;
      },
      {}
    );

    /* Processing data by dates */
    const datasetsData: UnixTypes[] = Object.values(datasetsByDate).sort(
      (a: UnixTypes, b: UnixTypes) => a.unix - b.unix
    );

    setSelectedDs(datasetsData[datasetsData.length - 1].ds!);
    let start: Moment = moment.unix(datasetsData[0].unix).startOf("month");
    let end: Moment = moment.unix(datasetsData[datasetsData.length - 1].unix).endOf("month");
    while (start <= end) {
      const unix: number = start.unix();
      if (!datasetsByDate[unix]) {
        datasetsByDate[unix] = { unix: unix };
      }
      start = start.add(1, "months");
    }

    /* Setting timeline data */
    const gd: UnixTypes[] = Object.values(datasetsByDate).sort(
      (a: UnixTypes, b: UnixTypes) => a.unix - b.unix
    );
    const twoYago: number = moment().subtract(2, "years").unix();
    setInitBrushState({
      startIndex: gd.findIndex((d: UnixTypes) => d.unix >= twoYago),
      endIndex: gd.length - 1,
    });
    setGraphData(gd);
  }, [datasets, ignoredData]);

  /* Hook keeping track of the selected measurement names */
  useEffect(() => {
    let selected: MeasurementNameIdTypes[] = [];
    let keys: string[] = Object.keys(selectedMeasurements);
    if (keys.length > 0) {
      keys.forEach((key: string) => {
        for (let i: number = 0; i < selectedMeasurements[key].names!.length; i++) {
          selected.push({
            name: selectedMeasurements[key].names![i],
            layerId: selectedMeasurements[key].layerId!,
            displayName: selectedMeasurements[key].displayNames![i],
          });
        }
      });
    }
    setSelectedNames(selected);
  }, [selectedMeasurements]);

  /* Hook monitoring whether the measurement of interest is still selected or changed */
  useEffect(() => {
    if (!(mainMeasurement.name === "No Preference")) {
      const found: boolean = selectedNames.some(
        (name) => name.name === mainMeasurement.name && name.layerId === mainMeasurement.layerId
      );
      if (!found) {
        setMainMeasurement({ name: "No Preference", layerId: 0, displayName: "No Preference" });
      }
    }
  }, [mainMeasurement, selectedNames]);

  /* Function handling folding and unfolding DatasetLists */
  const handleToggle = useCallback(
    (expanded, setExpanded, dataset) => {
      if (expanded) {
        let measurements: SelectedMeasurementsObjectTypes = { ...selectedMeasurements };
        delete measurements[`${dataset.uuid}`];
        setSelectedMeasurements(measurements);
      }
      setExpanded(!expanded);
    },
    [selectedMeasurements]
  );

  /* Function that filters measurement from a dataset with only a single value */
  const getCorrectMeasurements = useCallback((measurement: MeasurementTypes) => {
    const ignoredMeasurementNames: string[] = ["RGB", "Drainage lines", "id", "fid", "x", "y"];

    if (
      !ignoredMeasurementNames.includes(measurement.name) &&
      (measurement.min !== measurement.max ||
        (measurement.min === null && measurement.max === null))
    ) {
      return measurement;
    }
  }, []);

  /* Function for getting machine information */
  const getDevices = useCallback((dataset: DatasetTypes) => {
    let ds: DatasetTypes = { ...dataset };
    if (ds.devices) {
      return ds.devices;
    }
    ds.devices = ds.dataset_measurements.reduce(function (memo: any, m: MeasurementTypes) {
      let device: string | undefined = m.device;
      if (!device) {
        return memo;
      }
      // @ts-ignore   [Proper TS Solution Needed]
      let machineName: string = cropr.getDeviceName(device);
      if ($.inArray(machineName, memo) === -1) {
        memo.push(machineName);
      }
      return memo;
    }, []);
    return ds.devices;
  }, []);

  /* Function for getting the layer information of a dataset */
  const getLayers = useCallback((dataset: DatasetTypes) => {
    let ds: DatasetTypes = { ...dataset };
    if (ds.layers) {
      return ds.layers;
    }
    ds.layers = [];
    ds.data_set_rois.forEach(function (dsr: RoiTypes) {
      let cropfield: RoiTypes | GeometryModel = cropr.cropfields.findWhere({ did: dsr.roi });
      if (cropfield) {
        dsr.cropfield = cropfield;
        ds.layers!.push(dsr);
      }
    });
    return ds.layers;
  }, []);

  /* Growth Button handler toggling whether growth data should be displayed or not */
  const handleGrowth = useCallback(
    (event: any) => {
      let ignored: string[] = [...ignoredData];
      if (event.currentTarget.checked) {
        ignored.push(event.currentTarget.value);
        ignored.push("soil potential");
      } else {
        for (let i: number = 0; i < ignored.length; i++) {
          if (ignored[i] === event.currentTarget.value) {
            ignored.splice(i, 2);
            break;
          }
        }
      }
      setGrowthChecked(!growthChecked);
      setIgnoredData(ignored);
    },
    [growthChecked, ignoredData]
  );

  /* Height Button handler toggling whether height data should be displayed or not */
  const handleElevation = useCallback(
    (event: any) => {
      let ignored: string[] = [...ignoredData];
      if (event.currentTarget.checked) {
        ignored.push(event.currentTarget.value);
      } else {
        for (let i: number = 0; i < ignored.length; i++) {
          if (ignored[i] === event.currentTarget.value) {
            ignored.splice(i, 1);
            break;
          }
        }
      }
      setHeightChecked(!heightChecked);
      setIgnoredData(ignored);
    },
    [heightChecked, ignoredData]
  );

  /* Button handler validating if more then 2 datasets have been selected, after which they are processed */
  const handleSubmit = useCallback(() => {
    if (selectedNames.length > 1) {
      let measurementData: any = {
        cropfieldId: did,
        mainMeasurement: mainMeasurement,
        selectedMeasurements: selectedMeasurements,
        totalCorrelations: numOfTopCors,
      };
      onClick(measurementData);
      setKey("Correlations");
    } else {
      let error: JSX.Element = (
        <p style={{ color: "red" }}>{gettext("Please select 2 or more datasets!")}</p>
      );
      setNotEnoughDataError(error);
    }
  }, [
    did,
    mainMeasurement,
    numOfTopCors,
    onClick,
    selectedMeasurements,
    selectedNames.length,
    setKey,
  ]);

  /* Measurement preference dropdown menu */
  const measurementPreferenceDropDown: JSX.Element = (
    <DropdownButton
      alignRight
      title={mainMeasurement.displayName}
      id="dropdown-menu-align-left"
      onSelect={(event: string | null) => {
        let eventArray: string[] = event!.split(",");
        setMainMeasurement({
          name: eventArray[0],
          layerId: parseInt(eventArray[1]),
          displayName: eventArray[2],
        });
      }}
    >
      <div className="cor-dropdown">
        <Dropdown.Item eventKey="No Preference">{gettext("No Preference")}</Dropdown.Item>
        <Dropdown.Divider />
        {selectedNames.map((selectedName: MeasurementNameIdTypes, i: number) => (
          <Dropdown.Item
            // @ts-ignore   [Proper TS Solution Needed
            eventKey={[selectedName.name, selectedName.layerId, selectedName.displayName]}
            key={i}
          >
            {selectedName.displayName}
          </Dropdown.Item>
        ))}
      </div>
    </DropdownButton>
  );

  /* Element containing the disable buttons */
  const disableData: JSX.Element = (
    <div className="toggle-buttons-left">
      <ButtonGroup toggle className="pl-1" size="sm">
        <ToggleButton
          type="checkbox"
          checked={growthChecked}
          value="satellite"
          className="btn btn-primary btn-lg button-increase-right-margin"
          onChange={(event: any) => {
            handleGrowth(event);
          }}
        >
          {gettext("Growth")}
        </ToggleButton>
      </ButtonGroup>
      <ButtonGroup toggle className="pl-1" size="sm">
        <ToggleButton
          type="checkbox"
          checked={heightChecked}
          value="elevation"
          className="btn btn-primary btn-lg"
          onChange={(event: any) => {
            handleElevation(event);
          }}
        >
          {gettext("Height")}
        </ToggleButton>
      </ButtonGroup>
    </div>
  );

  /* Number of top correlations dropdown menu */
  const numOfTopCorsDropDown: JSX.Element = (
    <DropdownButton
      alignRight
      title={numOfTopCors}
      id="dropdown-menu-align-left"
      onSelect={(event: string | null) => {
        if (typeof event === "string") {
          setNumOfTopCors(parseInt(event));
        }
      }}
    >
      {[5, 10, 15, 20, 25].map((i) => (
        <Dropdown.Item key={i} eventKey={String(i)}>
          {i}
        </Dropdown.Item>
      ))}
    </DropdownButton>
  );

  /* Data submit button element */
  const submitButton: JSX.Element = (
    <button onClick={handleSubmit} className="btn btn-primary btn-lg">
      {gettext("Get Correlations")}
    </button>
  );

  /* Column storage */
  const columns: [string, string | JSX.Element][][] = [];

  /* Settings menu / column */
  const settingsCol: [string, string | JSX.Element][] = [
    ["", gettext("Custom Correlation Search")],
    [gettext("Do you have a preferred measurement?"), measurementPreferenceDropDown],
    [gettext("Toggle the buttons below to disable growth and/or height data"), disableData],
    [gettext("How many correlations would you like?"), numOfTopCorsDropDown],
    [gettext("Finished? Click the button below to get your correlations"), notEnoughDataError],
    ["", submitButton],
    [
      gettext("Change between datasets using the timeline"),
      selectedDs.length > 0 ? (
        <DataTimeline
          graphData={graphData}
          selectedDs={selectedDs}
          setSelectedDs={setSelectedDs}
          initBrushState={initBrushState}
        />
      ) : (
        ""
      ),
    ],
  ];
  columns.push(settingsCol);

  /* Data selection menu / column */
  const dataCol: [string, string | JSX.Element][] = [
    ["", gettext("Select the available measurements here:")],
  ];
  if (selectedDs.length > 0) {
    selectedDs.forEach((ds: DatasetTypes) => {
      let dsList: JSX.Element = (
        <DatasetList
          dataset={ds}
          getCorrectMeasurements={getCorrectMeasurements}
          getDevices={getDevices}
          getDisplayName={getDisplayName}
          getLayers={getLayers}
          handleToggle={handleToggle}
          key={ds.uuid}
          selectedCropfields={[did]}
          selectedMeasurements={selectedMeasurements}
          setSelectedMeasurements={setSelectedMeasurements}
        />
      );
      dataCol.push(["", dsList]);
    });
  } else {
    let noData: JSX.Element = (
      <div className="alert alert-info pt-2 mt-2">
        {gettext("All available data has been unselected...")}
      </div>
    );
    dataCol.push(["", noData]);
  }

  columns.push(dataCol);

  /* JSX */
  return (
    <div>
      <div className="row">
        {columns.map((col: [string, string | JSX.Element][], i: number) => (
          <div className="col p-3 border-right" key={i}>
            {i === 0 ? (
              col.map((row: [string, string | JSX.Element], j: number) => (
                <div className="fieldset pb-2" key={j}>
                  <label className="text-muted mb-0">{row[0]}</label>
                  <div className={"text header" + (j === 0 ? " font-weight-bold" : "")}>
                    {row[1]}
                  </div>
                </div>
              ))
            ) : (
              <div className="bg-white fix-overflow">
                {col.map((row: [string, string | JSX.Element], i: number) => (
                  <div className="fieldset pb-2" key={i}>
                    <label className="text-muted mb-0">{row[0]}</label>
                    <div className={"text header" + (i === 0 ? " font-weight-bold" : "")}>
                      {row[1]}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

/* Function determining the class of the dataset */
// TODO make this a map <name> --> datasetClass
const determineDsClass = (ds: DatasetTypes) => {
  if (ds.data_format === "r") {
    return "image";
  } else if (
    ["ISOBUS", "John Deere", "AVR", "Probotiq"].some((e: string) => ds.data_type.indexOf(e) > -1)
  ) {
    return "machine";
  } else if (
    ["Veris", "Dualem", "LIAB", "Fritzmeier", "PH"].some(
      (e: string) => ds.data_type.indexOf(e) > -1
    )
  ) {
    return "sensor";
  } else {
    return "other";
  }
};
